import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, useDeleteNotification } from '@/hooks';
import { Card, CardContent, Button, Skeleton } from '@/components/ui';
import { formatRelativeTime } from '@/lib/utils';
import { Bell, Check, CheckCheck, Trash2, MessageSquare, FileText, Info } from 'lucide-react';
import toast from 'react-hot-toast';

export function Notifications() {
  const [page, setPage] = useState(1);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const { data, isLoading, error } = useNotifications(page, 10, showUnreadOnly);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  const handleMarkRead = (id: number) => {
    markRead.mutate(id, {
      onError: () => toast.error('操作失败'),
    });
  };

  const handleMarkAllRead = () => {
    if (!confirm('确定要将所有通知标记为已读吗？')) return;
    markAllRead.mutate(undefined, {
      onSuccess: () => toast.success('已全部标记为已读'),
      onError: () => toast.error('操作失败'),
    });
  };

  const handleDelete = (id: number) => {
    if (!confirm('确定要删除此通知吗？')) return;
    deleteNotification.mutate(id, {
      onSuccess: () => toast.success('通知已删除'),
      onError: () => toast.error('操作失败'),
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'page_update':
        return <FileText className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-red-500">加载失败</p>
      </div>
    );
  }

  const notifications = data?.items || [];
  const unreadCount = data?.unreadCount || 0;

  return (
    <div className="max-w-4xl mx-auto py-12 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6" />
          通知
          {unreadCount > 0 && (
            <span className="text-sm font-normal bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant={showUnreadOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setShowUnreadOnly(!showUnreadOnly);
              setPage(1);
            }}
          >
            {showUnreadOnly ? '显示全部' : '仅未读'}
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              全部已读
            </Button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {showUnreadOnly ? '暂无未读通知' : '暂无通知'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className={notification.read ? 'opacity-70' : 'border-l-4 border-l-primary'}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="mt-1">{getNotificationIcon(notification.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">{notification.title}</p>
                        {notification.content && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {notification.content}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkRead(notification.id)}
                            title="标记为已读"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(notification.id)}
                          title="删除"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>{formatRelativeTime(notification.created_at)}</span>
                      {notification.data && (() => {
                        try {
                          const data = JSON.parse(notification.data);
                          if (data.page_slug) {
                            return (
                              <Link
                                to={`/pages/${data.page_slug}`}
                                className="text-primary hover:underline"
                              >
                                查看页面
                              </Link>
                            );
                          }
                        } catch {
                          // ignore
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <span className="py-2 px-4 text-sm text-muted-foreground">
            第 {page} / {data.totalPages} 页
          </span>
          <Button
            variant="outline"
            disabled={page === data.totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}
